import React, { ReactNode } from "react";
import {
  Routes,
  Route,
  Navigate,
  NavigateFunction,
  Params,
} from "react-router-dom";
import { Provider, observer } from "mobx-react";
import CheckOutProtectedRoutes from "./CheckOutProtectedRoutes";
import { Subject, takeUntil } from "rxjs";
import {
  CheckOutAfterPayPage,
  CheckOutItemsPage,
  CheckOutOptionsPage,
  CheckoutPayment,
  CheckoutSideBar,
  DetailPage,
} from "./components";
import {
  AuthService,
  Loader,
  ToastService,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import {
  CheckOutStore,
  CountryStore,
  PurchaseStore,
  RolesAndPermissionStore,
} from "../../shared/store";
import { CheckOutService } from "../../shared/services";
import {
  CheckoutColorType,
  MediaTypes,
  UpsertCheckoutActiveStep,
} from "../../shared/enums";
import { CheckoutCommonWrapper } from "../../shared/styles";
import { ICheckOutCombinedColors } from "../../shared/interfaces";
import { Header, Layout } from "../../shared/components";
import FullLibraryView from "../../shared/components/FullLibraryView";
import { toJS } from "mobx";
import {
  CheckOutAfterPayModel,
  CheckoutBrandingModel,
  CheckoutOptionsPageModel,
  DonationCheckoutModel,
  MediaModel,
} from "../../shared/models";
import { MediaItem } from "@mindmemobile/mm-shared-library";

interface IProps {
  checkoutStore: CheckOutStore;
  countryStore: CountryStore;
  navigate: NavigateFunction;
  params: Readonly<Params<string>>;
  rolesAndPermissionStore: RolesAndPermissionStore;
}

type IState = {
  showLibraryForDonation: boolean;
  showUploadForDonation?: boolean;
  showLibraryInAfterPay: boolean;
  showUploadInAfterPay?: boolean;
};

class CheckOutRoutes extends React.Component<IProps, IState> {
  private loader: Loader = new Loader(false);
  private $destroy: Subject<boolean> = new Subject<boolean>();

  constructor(props: IProps) {
    super(props);
    this.state = {
      showLibraryForDonation: false,
      showLibraryInAfterPay: false,
      showUploadForDonation: false,
      showUploadInAfterPay: false,
    };
  }

  toggleLibraryView = (show: boolean, fromUpload: boolean = false) => {
    this.setState(() => ({
      showLibraryForDonation: show,
      showUploadForDonation: fromUpload,
    }));
  };

  toggleLibraryInAfterPay = (show: boolean, fromUpload: boolean = false) => {
    this.setState(() => ({
      showLibraryInAfterPay: show,
      showUploadInAfterPay: fromUpload,
    }));
  };

  onCancel() {
    this.props.navigate("/app/checkouts");
  }

  componentDidMount(): void {
    this.props.checkoutStore?.reset();
    if (this.checkOutId) {
      this.loader.show();
      CheckOutService.getCheckOut(
        this.checkOutId,
        AuthService.authData.accountId,
      )
        .pipe(takeUntil(this.$destroy))
        .subscribe({
          next: (result) => {
            if (!result.result) {
              ToastService.showError("Error in loading checkout details");
            } else {
              this.loader.hide();
              this.props.checkoutStore?.constructStoreFromAPIModel(
                result.result,
              );
            }
          },
          error: () => {
            ToastService.showError("Error in loading checkout details");
          },
        });
    }
  }

  getColors = () => {
    const brandingData = this.props.checkoutStore.optionsPage.brandingData;
    if (!brandingData) return null;

    switch (brandingData.selectedColorType) {
      case CheckoutColorType.BasicColor:
        return brandingData.basicColors.getBasicColors() as ICheckOutCombinedColors;
      case CheckoutColorType.CustomColor:
        return brandingData.customColors.getCustomColors() as ICheckOutCombinedColors;
    }
  };

  get checkOutId() {
    const split = ((this.props.params && this.props.params["*"]) || "").split(
      "/",
    );
    const id = split[split.length - 1];
    if (UtilityMethods.isValidObjectId(id)) {
      return id;
    }
    return null;
  }

  get routes() {
    return (
      <CheckoutCommonWrapper>
        <Routes>
          <Route
            path={`detail/:id?`}
            element={
              <CheckOutProtectedRoutes
                currentRoute="detail"
                checkoutStore={this.props.checkoutStore}
              >
                <>
                  <DetailPage checkoutStore={this.props.checkoutStore} />
                  {/* <CheckoutPayment checkoutStore={this.props.checkoutStore} /> */}
                  {/* <CheckOutOptionsPage checkoutStore={this.props.checkoutStore} /> */}
                </>
              </CheckOutProtectedRoutes>
            }
          />
          <Route
            path={`/sell/:id?`}
            element={
              <CheckOutProtectedRoutes
                currentRoute="sell"
                checkoutStore={this.props.checkoutStore}
              >
                <CheckOutItemsPage
                  checkoutStore={this.props.checkoutStore}
                  purchaseStore={new PurchaseStore()}
                />
              </CheckOutProtectedRoutes>
            }
          />
          <Route
            path={`/pay/:id?`}
            element={
              <CheckOutProtectedRoutes
                currentRoute="pay"
                checkoutStore={this.props.checkoutStore}
              >
                <CheckoutPayment checkoutStore={this.props.checkoutStore} />
              </CheckOutProtectedRoutes>
            }
          />
          <Route
            path={`/afterpay/:id?`}
            element={
              <CheckOutProtectedRoutes
                currentRoute="afterpay"
                checkoutStore={this.props.checkoutStore}
              >
                <CheckOutAfterPayPage
                  checkoutStore={this.props.checkoutStore}
                  buttonBgColor={this.getColors()?.primary.rgba}
                  toggleLibrary={(show: boolean, fromUpload: boolean) =>
                    this.toggleLibraryInAfterPay(show, fromUpload)
                  }
                  showLibraryForDonation={this.state.showLibraryInAfterPay}
                />
              </CheckOutProtectedRoutes>
            }
          />
          <Route
            path={`/options/:id?`}
            element={
              <CheckOutProtectedRoutes
                currentRoute="options"
                checkoutStore={this.props.checkoutStore}
              >
                <CheckOutOptionsPage
                  checkoutStore={this.props.checkoutStore}
                  countryStore={this.props.countryStore}
                  toggleLibrary={(show: boolean, fromUpload: boolean) =>
                    this.toggleLibraryView(show, fromUpload)
                  }
                  showLibraryForDonation={this.state.showLibraryForDonation}
                />
              </CheckOutProtectedRoutes>
            }
          />

          <Route path="*" element={<Navigate to="/app/items" />} />
        </Routes>
      </CheckoutCommonWrapper>
    );
  }

  onBackOrNext(isNext: boolean) {
    if (this.loader.showLoader) return;
    if (isNext) {
      const success = this.props.checkoutStore.validate();
      if (!success) return;
    }

    const queryParam: string = this.checkOutId ? `/${this.checkOutId}` : "";
    switch (this.props.checkoutStore.activeStep) {
      case UpsertCheckoutActiveStep.Detail:
        this.props.checkoutStore.updateActiveStep(
          UpsertCheckoutActiveStep.Sell,
        );
        this.props.navigate(`/app/checkout/sell${queryParam}`);
        break;
      case UpsertCheckoutActiveStep.Sell:
        this.props.checkoutStore.updateActiveStep(
          isNext
            ? UpsertCheckoutActiveStep.Pay
            : UpsertCheckoutActiveStep.Detail,
        );
        isNext
          ? this.props.navigate(`/app/checkout/pay${queryParam}`)
          : this.props.navigate(`/app/checkout/detail${queryParam}`);

        break;
      case UpsertCheckoutActiveStep.Pay:
        this.props.checkoutStore.updateActiveStep(
          isNext
            ? UpsertCheckoutActiveStep.AfterPay
            : UpsertCheckoutActiveStep.Sell,
        );
        isNext
          ? this.props.navigate(`/app/checkout/afterpay${queryParam}`)
          : this.props.navigate(`/app/checkout/sell${queryParam}`);

        break;
      case UpsertCheckoutActiveStep.AfterPay:
        this.props.checkoutStore.updateActiveStep(
          isNext
            ? UpsertCheckoutActiveStep.Options
            : UpsertCheckoutActiveStep.Pay,
        );
        isNext
          ? this.props.navigate(`/app/checkout/options${queryParam}`)
          : this.props.navigate(`/app/checkout/pay${queryParam}`);

        break;
      case UpsertCheckoutActiveStep.Options:
        this.props.checkoutStore?.updateActiveStep(
          UpsertCheckoutActiveStep.AfterPay,
        );
        this.props.navigate(`/app/checkout/afterpay${queryParam}`);
        break;
    }
  }

  get showNextButton(): boolean {
    return (
      this.props.checkoutStore?.activeStep !== UpsertCheckoutActiveStep.Options
    );
  }

  get showBackbutton(): boolean {
    return (
      this.props.checkoutStore?.activeStep !== UpsertCheckoutActiveStep.Detail
    );
  }

  private stepConfig = {
    [UpsertCheckoutActiveStep.Detail]: {
      name: "detail",
      url: "/app/checkout/detail",
    },
    [UpsertCheckoutActiveStep.Sell]: {
      name: "sell",
      url: "/app/checkout/sell",
    },
    [UpsertCheckoutActiveStep.Pay]: {
      name: "payment",
      url: "/app/checkout/pay",
    },
    [UpsertCheckoutActiveStep.AfterPay]: {
      name: "after pay",
      url: "/app/checkout/afterpay",
    },
    [UpsertCheckoutActiveStep.Options]: {
      name: "options",
      url: "/app/checkout/options",
    },
  };

  onSave(isSaveAndExit: boolean = false) {
    if (this.loader.showLoader) return;

    if (isSaveAndExit) {
      const validationResult = this.props.checkoutStore?.validatePreviousSteps(
        UpsertCheckoutActiveStep.Options,
      );
      if (!validationResult) {
        ToastService.showError("Validation failed.");
        return;
      }

      const { isValid, errorStep } = validationResult;
      if (!isValid && errorStep) {
        ToastService.showError(
          `Please fill all the required fields in checkout ${this.stepConfig[errorStep].name} before saving.`,
        );
        this.props.checkoutStore?.updateActiveStep(errorStep);
        this.props.navigate(
          `${this.stepConfig[errorStep].url}/${this.checkOutId || ""}`,
        );
        return;
      }

      const isOptionsValid = this.props.checkoutStore?.validate(
        UpsertCheckoutActiveStep.Options,
      );
      if (!isOptionsValid) {
        this.props.checkoutStore?.updateActiveStep(
          UpsertCheckoutActiveStep.Options,
        );
        this.props.navigate(
          `${this.stepConfig[UpsertCheckoutActiveStep.Options].url}/${
            this.checkOutId || ""
          }`,
        );
        return;
      }
    } else {
      const success = this.props.checkoutStore?.validate();
      if (!success) return;
    }

    if (this.props.checkoutStore?.isSaveUpdateInProgress) return;
    this.props.checkoutStore?.toggleSaveUpdateInProgress();

    if (!this.props.checkoutStore) return;
    const request = this.props.checkoutStore.createAPIModel(
      this.checkOutId || "",
    );

    if (this.checkOutId) {
      CheckOutService.updateCheckOut(request).subscribe({
        next: (response) => {
          if (response) {
            ToastService.showSuccess("Checkout updated successfully");
            this.props.checkoutStore?.toggleSaveUpdateInProgress();
            if (isSaveAndExit) {
              this.props.navigate("/app/checkouts");
            }
          }
        },
        error: () => {
          ToastService.showError("Checkout update failed!");
          this.props.checkoutStore?.toggleSaveUpdateInProgress();
        },
      });
    } else {
      CheckOutService.saveCheckOut(request).subscribe({
        next: (response) => {
          if (response?.result) {
            ToastService.showSuccess("Checkout saved successfully");
            this.props.checkoutStore?.toggleSaveUpdateInProgress();
            if (isSaveAndExit) {
              this.props.navigate("/app/checkouts");
            }
          } else {
            this.props.checkoutStore?.toggleSaveUpdateInProgress();
          }
        },
        error: () => {
          ToastService.showError("Checkout save failed!");
          this.props.checkoutStore?.toggleSaveUpdateInProgress();
        },
      });
    }
  }

  updateMedia = (medias: MediaModel[]) => {
    const clonedData = new CheckoutOptionsPageModel({
      ...this.props.checkoutStore.optionsPage,
    });
    const clonedBrandingData = new CheckoutBrandingModel({
      ...this.props.checkoutStore.optionsPage.brandingData,
    });
    clonedBrandingData["donationCheckout"] = new DonationCheckoutModel({
      ...this.props.checkoutStore.optionsPage.brandingData.donationCheckout,
      media: medias,
    });
    this.props.checkoutStore.updateOptionsPage(clonedData);
  };

  onDelete = (media: MediaModel) => {
    const clonedData = new CheckoutOptionsPageModel({
      ...this.props.checkoutStore.optionsPage,
    });

    const clonedBrandingData = new CheckoutBrandingModel({
      ...clonedData.brandingData,
    });

    const donationCheckout = new DonationCheckoutModel({
      ...clonedData.brandingData.donationCheckout,
      media: clonedData.brandingData.donationCheckout.media.filter(
        (m) => m.mediaId !== media.mediaId,
      ),
    });

    clonedBrandingData["donationCheckout"] = donationCheckout;
    clonedData["brandingData"] = clonedBrandingData;

    this.props.checkoutStore.updateOptionsPage(clonedData);
  };

  onSelectLibraryMedia = (media: MediaItem) => {
    const clonedData = new CheckoutOptionsPageModel({
      ...this.props.checkoutStore.optionsPage,
    });

    const clonedBrandingData = new CheckoutBrandingModel({
      ...clonedData.brandingData,
    });

    const donationCheckout = new DonationCheckoutModel({
      ...clonedData.brandingData.donationCheckout,
      media: [
        ...clonedData.brandingData.donationCheckout.media,
        MediaModel.mediaItemToMediaModel(media),
      ],
    });

    clonedBrandingData["donationCheckout"] = donationCheckout;
    clonedData["brandingData"] = clonedBrandingData;

    this.props.checkoutStore.updateOptionsPage(clonedData);
  };

  onFavourite = (media: MediaModel) => {
    const clonedData = new CheckoutOptionsPageModel({
      ...this.props.checkoutStore.optionsPage,
    });

    const clonedBrandingData = new CheckoutBrandingModel({
      ...clonedData.brandingData,
    });

    const donationCheckout = new DonationCheckoutModel({
      ...clonedData.brandingData.donationCheckout,
      media: clonedData.brandingData.donationCheckout.media.map((m) => {
        if (m.mediaId === media.mediaId) {
          return new MediaModel({ ...m, isFavourite: !m.isFavourite });
        }
        return m;
      }),
    });

    clonedBrandingData["donationCheckout"] = donationCheckout;
    clonedData["brandingData"] = clonedBrandingData;

    this.props.checkoutStore.updateOptionsPage(clonedData);
  };

  onTabChange = (key: string) => {
    const step = key as UpsertCheckoutActiveStep;
    const queryParam = this.checkOutId || "";

    const validationResult =
      this.props.checkoutStore.validatePreviousSteps(step);
    if (!validationResult) {
      ToastService.showError("Unable to validate steps.");
      return;
    }

    const { isValid, errorStep } = validationResult;
    if (!isValid && errorStep) {
      ToastService.showError(
        `Please fill all the required fields in checkout ${this.stepConfig[errorStep].name} before proceeding.`,
      );
      this.props.checkoutStore?.updateActiveStep(errorStep);
      this.props.navigate(`${this.stepConfig[errorStep].url}/${queryParam}`);
      return;
    }

    this.props.checkoutStore.updateActiveStep(step);
    this.props.navigate(`${this.stepConfig[step].url}/${queryParam}`);
  };

  updateMediaForAfterPay(media?: MediaItem) {
    const clonedState = new CheckOutAfterPayModel({
      ...this.props.checkoutStore.afterPayPage,
    });
    clonedState.media = media
      ? MediaModel.mediaItemToMediaModel(media)
      : undefined;
    this.props.checkoutStore.updateAfterPayPage(clonedState);
  }

  onUploadMediaInAfterPay(media?: MediaModel) {
    const clonedState = new CheckOutAfterPayModel({
      ...this.props.checkoutStore.afterPayPage,
    });
    clonedState.media = media;
    this.props.checkoutStore.updateAfterPayPage(clonedState);
  }

  render(): ReactNode {
    if (!this.props.countryStore.countryDataLoaded) {
      return <>{new Loader().loaderComponent}</>;
    }
    if (this.state.showLibraryForDonation) {
      return (
        <FullLibraryView
          multiple={false}
          medias={toJS(
            this.props.checkoutStore?.optionsPage.brandingData.donationCheckout
              .media || [],
          )}
          onUpload={(medias: MediaModel[]) => this.updateMedia(medias)}
          onDelete={(media: MediaModel) => this.onDelete(media)}
          onSelectLibraryMedia={(media) => this.onSelectLibraryMedia(media)}
          onFavourite={(media: MediaModel) => this.onFavourite(media)}
          allowedMediaTypes={[MediaTypes.Image]}
          toggleLibrary={(show: boolean, fromUpload?: boolean) =>
            this.toggleLibraryView(show, fromUpload)
          }
          showLibrary={this.state.showLibraryForDonation}
          updateMedia={(medias: MediaModel[]) => this.updateMedia(medias)}
          showUpload={this.state.showUploadForDonation}
        />
      );
    }
    if (this.state.showLibraryInAfterPay) {
      return (
        <FullLibraryView
          multiple={false}
          medias={
            (this.props.checkoutStore.afterPayPage.media && [
              this.props.checkoutStore.afterPayPage.media,
            ]) ||
            []
          }
          onUpload={(medias: MediaModel[]) =>
            this.onUploadMediaInAfterPay(medias[0])
          }
          onDelete={() => this.updateMediaForAfterPay(undefined)}
          onSelectLibraryMedia={(media) => this.updateMediaForAfterPay(media)}
          allowedMediaTypes={[MediaTypes.Image, MediaTypes.Video]}
          toggleLibrary={(show) => this.toggleLibraryInAfterPay(show)}
          showLibrary={this.state.showLibraryInAfterPay}
          updateMedia={() => {}}
          showUpload={this.state.showUploadInAfterPay}
        />
      );
    }

    return (
      <Provider>
        <Layout
          isFromCheckout={
            this.props.checkoutStore?.activeStep ===
            UpsertCheckoutActiveStep.Detail
              ? false
              : true
          }
          footerPadding={
            this.props.checkoutStore?.activeStep ===
            UpsertCheckoutActiveStep.Detail
              ? "5px 0px 35px"
              : "25px 0px 35px"
          }
          // padding="30px 0px 50px 50px"// as per design
          padding="0px"
          sideBarComponent={
            <CheckoutSideBar
              activeStep={this.props.checkoutStore.activeStep}
              showVariant={false}
              onTabChange={(key: string) => this.onTabChange(key)}
              checkoutStore={this.props.checkoutStore}
            />
          }
          showCancelButton={false}
          headerCaption={this.props.checkoutStore?.headerCaption || ""}
          cancelClick={() => this.onCancel()}
          nextButtonCaption={this.showNextButton ? "Next" : ""}
          nextClick={() => this.onBackOrNext(true)}
          showBackButton={this.showBackbutton}
          showNextButton={this.showNextButton}
          backClick={() => this.onBackOrNext(false)}
          saveAndExitClick={() => this.onSave(true)}
          showSaveAndExit={this.checkOutId ? true : false}
          disableSaveAndExit={
            this.loader.showLoader ||
            !!this.props.checkoutStore?.isSaveUpdateInProgress
          }
          showHeader={false}
          headerComponent={
            <Header
              headerCaption="CREATE YOUR CHECKOUT PAGE"
              showSaveButton={!this.showNextButton}
              saveAndExitButtonCaption={
                this.showNextButton ? "NEXT" : "SAVE & EXIT"
              }
              saveClick={() => this.onSave()}
              cancelClick={() => this.onCancel()}
              saveAndExitClick={() =>
                this.showNextButton
                  ? this.onBackOrNext(this.showNextButton)
                  : this.onSave(true)
              }
              showSaveAndExitButton={true}
              disableSaveAndExitButton={
                this.loader.showLoader ||
                !!this.props.checkoutStore?.isSaveUpdateInProgress
              }
            />
          }
        >
          {this.loader.showLoader ? this.loader.loaderComponent : this.routes}
        </Layout>
      </Provider>
    );
  }
}

export default withRouter(observer(CheckOutRoutes));
